import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query'

import { FC } from '../../../Services/FeathersClient'

/**
 * Accesso al servizio `agentEval` di BusinessAI, proxy verso il motore nell'Executor.
 * Solo in sviluppo: fuori, il server risponde «non disponibile» e la pagina lo dice.
 */

const SERVIZIO = 'agentEval'

/**
 * Il catalogo degli scenari (file JSON nel repo), con quelli non validi. Con un environment
 * porta anche il suo flusso e segna gli scenari che lì non girerebbero.
 * @param {string|number} [environmentId] - L'environment scelto nel form
 * @param {string} [flusso] - Il flusso della chat simulata, se diverso da quello dell'environment
 */
export const useScenari = (environmentId, flusso) =>
  useQuery({
    queryKey: ['agentEval', 'scenari', environmentId || null, flusso || null],
    queryFn: () =>
      FC.service(SERVIZIO).find({
        query: {
          cosa: 'scenari',
          ...(environmentId ? { environmentId } : {}),
          ...(flusso ? { flusso } : {})
        }
      }),
    placeholderData: (precedente) => precedente,
    retry: false
  })

/**
 * Il catalogo letto per ciascun environment scelto, nell'ordine: serve al piano della campagna
 * (cosa gira dove) e alla stima. Stessa chiave di `useScenari`, quindi la cache è condivisa.
 * @param {Array<string|number>} environmentIds
 * @returns {Array<{environmentId: number, catalogo?: Object, isLoading: boolean, isError: boolean}>}
 */
export const useScenariPerEnvironment = (environmentIds = []) =>
  useQueries({
    queries: environmentIds.map((environmentId) => ({
      queryKey: ['agentEval', 'scenari', String(environmentId), null],
      queryFn: () => FC.service(SERVIZIO).find({ query: { cosa: 'scenari', environmentId } }),
      retry: false
    }))
  }).map((query, indice) => ({
    environmentId: Number(environmentIds[indice]),
    catalogo: query.data,
    isLoading: query.isLoading,
    isError: query.isError
  }))

/**
 * Una pagina di run, filtrata dal server. Si rilegge ogni 3 secondi mentre una gira, ogni
 * 15 altrimenti: è così che la pagina si accorge anche di una run lanciata dal terminale.
 * @param {{pagina: number, filtri: Object, finestra?: string}} richiesta
 */
export const useRuns = ({ pagina, filtri, finestra, segue }) =>
  useQuery({
    queryKey: ['agentEval', 'runs', pagina, filtri, finestra || 'tutto'],
    queryFn: () =>
      FC.service(SERVIZIO).find({
        query: {
          cosa: 'runs',
          pagina,
          ...(finestra && finestra !== 'tutto' ? { finestra } : {}),
          ...(filtri.testo ? { testo: filtri.testo } : {}),
          ...(filtri.environmentId ? { environmentId: filtri.environmentId } : {}),
          ...(filtri.flusso ? { flusso: filtri.flusso } : {}),
          ...(filtri.stato ? { stato: filtri.stato } : {}),
          ...(filtri.versioneHarness ? { versioneHarness: filtri.versioneHarness } : {}),
          ...(filtri.dal ? { dal: filtri.dal } : {}),
          ...(filtri.al ? { al: filtri.al } : {})
        }
      }),
    placeholderData: (precedente) => precedente,
    // `segue`: una run appena lanciata. La riga può non essere ancora sul database, e
    // senza questo il poll resterebbe a 15 secondi finché un refresh non la vede.
    refetchInterval: (query) =>
      segue || query.state.data?.runInCorso || query.state.data?.inCoda ? 2000 : 15000,
    retry: false
  })

/**
 * Una run completa, con le esecuzioni. Si rilegge mentre è in corso.
 * @param {string|null} id - La run scelta
 */
export const useRun = (id) =>
  useQuery({
    queryKey: ['agentEval', 'run', id],
    queryFn: () => FC.service(SERVIZIO).get(id),
    enabled: Boolean(id),
    refetchInterval: (query) =>
      query.state.data?.stato === 'in_corso' || query.state.data?.stato === 'in_coda'
        ? 2000
        : false,
    retry: false
  })

/** Ferma la run in corso: i risultati già raccolti restano. */
export const useFermaRun = () => {
  const queryClient = useQueryClient()
  return async (id) => {
    await FC.service(SERVIZIO).patch(id, { azione: 'ferma' })
    await queryClient.invalidateQueries({ queryKey: ['agentEval'] })
  }
}

/** Toglie una run e ricarica l'elenco. Una in corso si ferma prima. */
export const useEliminaRun = () => {
  const queryClient = useQueryClient()
  return async (id) => {
    const esito = await FC.service(SERVIZIO).remove(id)
    await queryClient.invalidateQueries({ queryKey: ['agentEval'] })
    return esito
  }
}

/** Avvia una run e ricarica elenco e catalogo. */
export const useAvviaRun = () => {
  const queryClient = useQueryClient()
  return async (corpo) => {
    const avviata = await FC.service(SERVIZIO).create(corpo)
    await queryClient.invalidateQueries({ queryKey: ['agentEval'] })
    return avviata
  }
}
